import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import axios from 'axios';
import MultiPose from '../../PackageBuilders/MultiPose';
import { useCustomer } from '../../../contexts/CustomerContext';
import { getAppropriateBg } from '../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import PortraitsGallery from '../../PortraitsGallery';
import useBuyAllOption from '../../../hooks/useBuyAllOption';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useBackgrounds from '../../../hooks/useBackgrounds';
import usePoses from '../../../hooks/usePoses';
import usePreorderPose from '../../../hooks/usePreorderPose';
import useQueryString from '../../../hooks/use-query-string';
import routes from '../../../routes';
import { OrderPackage } from '../../../models';
import OptionSelectionContent from '../OptionSelection/OptionSelectionContent';
import usePricelist from '../../../hooks/usePricelist';
import useRetouchingExtra from '../../../hooks/useRetouchingExtra';
import useAddons from '../../../hooks/useAddons';
import QueryKeys from '../../../utils/QueryKeys';
import SinglePoseMutator from './SinglePoseMutator';
import useImageTypes from '../../../hooks/useImageTypes';
import useMultiSeasonPoses from "../../../hooks/useMultiSeasonPoses";
import useHolidayPricelist from "../../../hooks/useHolidayPricelist";
import GalleryIntro from '../../GalleryIntro';
import gradeLabel, { portraitsHeadline } from '../../../utils/gradeLabel';
import usePackagePoses from '../../../hooks/usePackagePoses';
import useGrowthThumbnails from '../../../hooks/useGrowthThumbnails';

export default function MultiPosePackageBuilder() {
  // Routing and queries
  const { packageId } = useParams();
  const history = useHistory();
  const {
    step,
    opt: optionId,
    pastImages,
    favorites,
    preorderPoseId,
    type,
  } = useQueryString();
  const favoriteIds = [favorites].flat().filter((id) => !!id).map((id) => id.toString());

  // Data
  const { customer, setCustomer } = useCustomer();
  const [orderPackage] = useState(new OrderPackage());
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: pricelist } = usePricelist(packageId);
  const { data: poses } = pricelist?.id && (pricelist?.multipleSeasons?.length || pricelist?.displaySharedPoses) ? (
      useMultiSeasonPoses(customer.currentStudent.id, packageId)
    ) : (
    usePoses(customer.currentStudent.id, packageId, pastImages)
  )

  const { data: preorderPose } = usePreorderPose(preorderPoseId)

  useRetouchingExtra();
  const { data: addons } = useAddons(true);

  const { data: imageTypes } = useImageTypes(optionId);
  const { data: packagePoses } = usePackagePoses(packageId);
  const { data: buyAllOption } = useBuyAllOption(packageId, customer?.currentSchool?.id);
  const [currentSeason] = useLocalStorage(`currentSeason-${customer?.currentStudent?.id}`);

  // "Growing Up" header thumbnails (last year + this year). Keyed off the season the current
  // poses were shot in; the server returns nulls when there is no prior-year portrait.
  const currentFolder = (poses || [])[0]?.folder;
  const { data: growth } = useGrowthThumbnails(customer?.currentStudent?.id, currentFolder);

  // The intro splash — the same one the senior flow plays, with the student's GRADE above
  // their name instead of "Class of 20XX". Session state, not a persisted "seen" flag.
  const [introDone, setIntroDone] = useState(false);

  const favoritePoses = favoriteIds.map((id) => (poses || []).find((pose) => pose.id === id));

  // UI
  const isFetchingPoses = useIsFetching({ queryKey: [QueryKeys.STUDENT_IMAGES] });
  const isFetchingBackgrounds = useIsFetching({ queryKey: [QueryKeys.BACKGROUNDS] });
  const isFetchingPricelist = useIsFetching({ queryKey: [QueryKeys.PRICELIST] });
  const { data: giftPricelist } = useHolidayPricelist(customer.currentSchool?.id);

  const isFetching = isFetchingPoses || isFetchingBackgrounds || isFetchingPricelist;

  // "Download All My Photos" — the same POST + confirm redirect the old favorites screen
  // did. Kept identical: it is a live revenue path, not part of the redesign.
  const handleBuyAll = async () => {
    const response = await axios.post(
      `/carts/${customer?.cart?.id}/buy_all`,
      {
        package_id: packageId,
        option_id: buyAllOption.id,
        student_id: customer.currentStudent.id,
        season: currentSeason,
      },
    );
    history.push(routes.confirm(response.data?.order_package_id));
  };

  const onSave = () => {
    setCustomer({ ...customer, currentOrderPackage: orderPackage });

    if (giftPricelist) history.push(routes.holiday.builder({ optional: true, o: orderPackage.id, pid: orderPackage.packageId }))
    else if (addons.length === 0) history.push(routes.retouching(orderPackage.id));
    else history.push(routes.addons(orderPackage.id));
  };

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  if (!(packageId && customer.currentSchool && customer.currentStudent)) return null;
  if (isFetching) return null;
  if (backgrounds.length === 0) return null;
  if (poses?.length === 0 && !preorderPose) return null;

  const background = backgrounds[0];

  // "Growing Up" flanking thumbnails. Both years share this season's backdrop, so the child's
  // growth is the only thing that changes between them. Only shown when a prior-year portrait
  // exists — the server returns previous: null (and current: null) otherwise, so both are null.
  const growthBackdrop = backgrounds?.[0]?.imageUrl;
  const leadThumbnail = growth?.previous
    ? {
      foregroundUrl: growth.previous.watermark_url,
      backgroundUrl: growthBackdrop,
      // "2nd Grade" under last year's photo; omitted when the grade isn't one we can name.
      caption: gradeLabel(growth.previous.grade),
    }
    : null;
  const trailThumbnail = growth?.previous && growth?.current
    ? {
      foregroundUrl: growth.current.watermark_url,
      backgroundUrl: growthBackdrop,
      caption: gradeLabel(growth.current.grade),
    }
    : null;

  const isAllJpg = (poses || []).reduce((acc, cur) => acc && !cur.extension.toLowerCase().includes('png'), true);

  // Intro cards: each pose on the backdrop the pricelist's package-poses spec gives it
  // (seniors are the only audience that overrides the backdrop with the school's own).
  const introPhotos = (poses || []).map((pose) => ({
    foregroundUrl: pose.imageUrl || pose.watermarkUrl,
    backgroundUrl: getAppropriateBg(packagePoses, pose, backgrounds, pricelist)?.imageUrl
      || backgrounds?.[0]?.imageUrl,
  }));
  const introPreloadUrls = introPhotos
    .flatMap((photo) => [photo.foregroundUrl, photo.backgroundUrl])
    .filter(Boolean);

  // Plays once, on first entry to the pose picker.
  const showIntro = (!step || step === 'pose-selection') && !introDone && introPhotos.length > 0;

  return (
    <div>
      {showIntro && (
        <GalleryIntro
          studentFirstName={customer.currentStudent?.firstName}
          // Grade, not class year: "3rd Grade". gradeLabel returns null for staff and for
          // grades it can't name, and the intro omits the line entirely rather than guess.
          eyebrow={gradeLabel(customer.currentStudent?.grade)}
          allPhotos={introPhotos}
          preloadUrls={introPreloadUrls}
          onComplete={() => setIntroDone(true)}
        />
      )}
      {(!step || step === 'pose-selection') && (
        <PortraitsGallery
          poses={poses}
          backgrounds={backgrounds}
          packageId={packageId}
          // "Melina's Third Grade Portraits" — the grade is dropped rather than guessed
          // when we can't name it (staff, unknown), so the headline is never wrong.
          headline={portraitsHeadline(customer.currentStudent?.firstName, customer.currentStudent?.grade)}
          // Trial theme tagline under the headline (elementary only for now).
          subhead="Look who's growing up"
          // Last year's / this year's portrait flanking the title (present only when a
          // prior-year photo exists).
          leadThumbnail={leadThumbnail}
          trailThumbnail={trailThumbnail}
          // No `yearbook` prop: favorites only. No cap buttons, no eligibility gate, no
          // step 1 — and backgrounds come from the pricelist's package-poses spec, since
          // seniors are the only audience that overrides the backdrop.
          favoritesKey={`favorites:${customer.currentStudent?.id}:${packageId}`}
          // Hidden for an all-JPG session, exactly as the old screen did: those poses have
          // their background baked in and aren't sold as the digital "all my photos" set.
          promo={buyAllOption?.buyAllBannerUrl && !isAllJpg ? {
            imageUrl: buyAllOption.buyAllBannerUrl,
            label: 'Download all my photos',
            onClick: handleBuyAll,
          } : null}
          continueLabel="Choose your package"
          onContinue={(chosen) => history.push(
            routes.elementaryMultiPoseBuilder(packageId, { favorites: chosen, step: 'select-package' }),
          )}
        />
      )}
      {step === 'select-package' && pricelist && (
        <OptionSelectionContent
          orderPackage={orderPackage}
          foreground={favoritePoses.length > 0 ? favoritePoses[0].watermarkUrl : (preorderPose || poses[0]).watermarkUrl}
          background={background}
          multiPoseHandler={(selectedOpt) => history.push(routes.elementaryMultiPoseBuilder(packageId, { favorites: favoriteIds, preorderPoseId: preorderPoseId, opt: selectedOpt.id, step: 'build' }))}
          packageId={packageId}
          accessCodeOnly={false}
          preorderPose={preorderPose}
        />
      )}
      {step === 'build' && poses.length === 1 && isAllJpg && (
        <SinglePoseMutator
          cartId={customer.cart.id}
          onSave={onSave}
          imageTypes={imageTypes}
          orderPackage={orderPackage}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          schoolId={customer.currentSchool.id}
          packageId={packageId}
          poses={poses}
        />
      )}
      {step === 'build' && (poses.length > 1 || !isAllJpg || preorderPose) && (
        <MultiPose
          cartId={customer.cart.id}
          packageId={packageId}
          schoolId={customer.currentSchool.id}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          onSave={onSave}
          favoriteIds={favoriteIds}
          // This is the favorites-picker flow (URL `favorites=`), so restrict the builder
          // to the chosen poses. Other MultiPose callers (senior, edit) leave this false
          // and keep the full session -- their favoriteIds only sort/highlight.
          restrictToFavorites
          orderPackage={orderPackage}
          pastImages={pastImages}
          preorderPose={preorderPose}
        />
      )}
    </div>
  );
}
